<template>
  <TourStepsTemplate
    :callbacks="myCallbacks"
    :class="`report-intro-part report-intro-partV2__step-${reportIntroStep}`"
    :steps="steps"
    tour-name="reportIntroV2"
  />
</template>

<script>
// components
import TourStepsTemplate from '@/pages/report/components/onboarding/TourStepsTemplate'
// Utils
import { scrollToSelector } from '@/utils/scroll-to'
// services
import {
  onStopReportOnboardingTour,
  filterSteps,
} from '@/services/onboarding-tour'
// mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

export default {
  name: 'ReportIntroPartV2',
  components: {
    TourStepsTemplate,
  },
  mixins: [deviceWidthMixin],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      myCallbacks: {
        onSkip: this.onSkipFinishTour,
        onFinish: this.onSkipFinishTour,
      },
      steps: [],
    }
  },
  computed: {
    reportIntroStep() {
      this.loading
      return this.$tours['reportIntroV2']?.currentStep
    },
  },
  watch: {
    reportIntroStep: {
      handler(newValue) {
        if (newValue == -1) {
          this.hideAllHighlights()
        }
      },
      immediate: true,
    },
    isMobile() {
      this.steps = this.buildSteps()
    },
  },
  mounted() {
    this.steps = this.buildSteps()
  },
  beforeDestroy() {
    this.hideAllHighlights()
  },
  methods: {
    scrollToSelector,
    onStopReportOnboardingTour,
    filterSteps,
    buildSteps() {
      const offsetFirst = {
        name: 'offset',
        options: { offset: this.isMobile ? [0, -8] : [0, 10] },
      }
      const offsetRest = {
        name: 'offset',
        options: { offset: [0, 10] },
      }
      const baseParams = (overrides = {}) => ({
        placement: 'top',
        enableScrolling: false,
        strategy: 'absolute',
        modifiers: [offsetRest],
        ...overrides,
      })
      const steps = [
        {
          target: '#address',
          title: `Report is ready! 🎉`,
          content: `<span class="bold-600">Let’s walk through</span> what you can uncover about this address`,
          params: baseParams({ modifiers: [offsetFirst] }),
          before: () => {
            this.prepareSteps()
            this.hideAllHighlights()
            this.highlightSelectorId({
              selector: 'address',
            })
            return Promise.resolve()
          },
        },
        {
          target: '#risks',
          title: 'Check Address Risk 🛡️',
          content: 'Reveal how risky this address is and who exactly owns it',
          offset: 1,
          params: baseParams(),
          before: () => {
            this.hideAllHighlights()

            this.prepareSteps()
            this.scrollToSelector('#main-container', '#risks', 300)

            this.highlightSelectorId({
              selector: 'risks',
            })

            return Promise.resolve()
          },
        },
        {
          target: '#tier2',
          title: `Trace the Money Path 🧭`,
          content: `Follow exactly where the money came from and where it went`,
          offset: 1,
          params: baseParams(),
          before: () => {
            this.prepareSteps()
            this.hideAllHighlights()
            this.scrollToSelector('#main-container', '#tier2', 300)

            this.highlightSelectorId({
              selector: 'tier2',
            })

            return Promise.resolve()
          },
        },
        {
          target: '#full',
          title: `Unlock the Full Transaction Ledger 🚀`,
          content: `Review all <span class="bold-600">Received From</span> and <span class="bold-600">Sent To</span> transactions to see every owner and risk behind this address`,
          offset: 1,
          params: baseParams(),
          before: () => {
            this.prepareSteps()
            this.hideAllHighlights()
            this.scrollToSelector('#main-container', '#full', 300)

            this.highlightSelectorId({
              selector: 'full',
            })

            return Promise.resolve()
          },
        },
        {
          target: '#login-button',
          title: `Let's start by logging in`,
          content: `Please log in or sign up.`,
          offset: 1,
          params: baseParams({ placement: 'bottom' }),
          before: () => {
            this.hideAllHighlights()
            this.scrollToTop({ top: 0 })
            return Promise.resolve()
          },
        },
      ]
      return steps
    },
    prepareSteps() {
      this.filterSteps({
        vm: this,
      })
    },
    hideAllHighlights() {
      ['address', 'risks', 'tier2', 'full'].forEach(selector =>
        this.unhighlightSelectorId({ selector }),
      )
    },
    scrollToTop({ top = 0 } = {}) {
      document.getElementById('main-container')?.scrollTo({
        top,
        behavior: 'smooth',
      })
    },
    highlightSelectorId({ selector } = {}) {
      const el = document.getElementById(selector)
      el?.classList.add('highlight-section')
    },
    unhighlightSelectorId({ selector } = {}) {
      const el = document.getElementById(selector)
      el?.classList.remove('highlight-section')
    },
    async onSkipFinishTour() {
      this.onStopReportOnboardingTour({ vm: this, tourName: 'reportIntroV2' })
    },
  },
}
</script>

<style scoped>
.report-intro-partV2__step-0::v-deep .v-step {
  max-width: min(280px, 95vw);
}
.report-intro-partV2__step-1::v-deep .v-step {
  max-width: min(269px, 95vw);
}
.report-intro-partV2__step-2::v-deep .v-step {
  max-width: min(310px, 95vw);
}
.report-intro-partV2__step-3::v-deep .v-step {
  max-width: min(380px, 95vw);
}
.report-intro-partV2__step-4::v-deep .v-step {
  max-width: min(280px, 95vw);
}
</style>
